import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "../context/AppContext";
import { 
  trackChatOpen, 
  trackMessageSent, 
  trackChatClose, 
  trackQuickReply,
  MobileTrackingUtils,
  getDeviceType 
} from "../utils/chatAnalytics";
import { trackChatEvent, getChatSessionId } from "../utils/chatTelemetry";
import avatarSrc from "./f06bea43-84ef-48a8-916c-77cdf3b94e74.png"; // Path to the uploaded avatar
import "./AgenticChatLauncher.css";
import StructuredBlocks from './StructuredBlocks';
import { isStructuredBlockEvent, mergeBlocks, blocksToFallbackText, mergeBlockText } from '../utils/structuredBlocks';

const DEFAULT_COLORS = {
  neonA: "#FF4B1F", // Ferrari red
  neonB: "#FF6F00", // McLaren orange
  bg: "#0B0F14",   // near-black panel
  bgSoft: "#11161C",
  text: "#EDEFF2",
  textMut: "#A4AAAE",
  border: "rgba(255, 111, 0, 0.35)",
};

export default function AgenticChatLauncher({
  title = "Twisted Cloud Agent",
  placeholder = "Type your message…",
  quickReplies = ["AI Integration", "Intelligent Business Networks", "Mobile Applications"],
  onSend = (text) => console.log("send:", text),
  colors = DEFAULT_COLORS,
}) {
  const { isChatOpen, openChat, closeChat } = useAppContext();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => [
    createChatMessage("agent", "Hello, how can I assist you today?")
  ]);
  const [typing, setTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === "undefined" ? 0 : window.innerHeight));
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [showTimestamps, setShowTimestamps] = useState(false); // Toggle for timestamp display
  const [isListening, setIsListening] = useState(false);
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(true); // Privacy consent banner state
  const [portalNode, setPortalNode] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [layoutMode, setLayoutMode] = useState("desktop");
  const [isMinimized, setIsMinimized] = useState(false);
  const [liveAnnouncement, setLiveAnnouncement] = useState("");
  const panelRef = useRef(null);
  const textareaRef = useRef(null);
  const chatOpenTimeRef = useRef(null);
  const recognitionRef = useRef(null);
  const speechBaseInputRef = useRef("");
  const keyboardStateRef = useRef(false);
  const baselineHeightRef = useRef(typeof window === "undefined" ? 0 : window.innerHeight);

  const autoSizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "auto";
    const nextHeight = Math.min(textarea.scrollHeight, 150);
    textarea.style.height = `${nextHeight}px`;
  }, []);

  const activeRequestControllerRef = useRef(null);
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  const evaluateLayoutMode = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const width = window.innerWidth || 0;
    const nextMode = width <= 480 ? "mobile" : width <= 1024 ? "tablet" : "desktop";
    setLayoutMode(nextMode);

  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    let portalRoot = document.getElementById("twistedcloud-agent-chat-root");
    let createdRoot = false;

    if (!portalRoot) {
      portalRoot = document.createElement("div");
      portalRoot.setAttribute("id", "twistedcloud-agent-chat-root");
      createdRoot = true;
    }

    const mountTarget = document.documentElement || document.body;
    mountTarget.appendChild(portalRoot);

    console.debug("AgenticChatLauncher: portal mounted", {
      createdRoot,
      portalId: portalRoot.id,
      mountTargetTag: mountTarget?.tagName,
      timestamp: Date.now()
    });

    const portalStyles = {
      position: "fixed",
      top: "0",
      right: "0",
      bottom: "0",
      left: "0",
      zIndex: "2147483646",
      overflow: "visible",
      pointerEvents: "none",
      transform: "none"
    };

    const previousStyles = Object.keys(portalStyles).reduce((accumulator, key) => {
      accumulator[key] = portalRoot.style[key];
      return accumulator;
    }, {});

    const previousDataAttribute = portalRoot.getAttribute("data-agentic-chat");

    Object.assign(portalRoot.style, portalStyles);
    portalRoot.setAttribute("data-agentic-chat", "true");

    setPortalNode(portalRoot);

    return () => {
      console.debug("AgenticChatLauncher: portal cleanup", {
        createdRoot,
        portalId: portalRoot.id,
        mountTargetTag: mountTarget?.tagName,
        timestamp: Date.now()
      });
      setPortalNode(null);
      if (createdRoot && portalRoot.parentNode) {
        portalRoot.parentNode.removeChild(portalRoot);
      } else {
        Object.keys(portalStyles).forEach((key) => {
          portalRoot.style[key] = previousStyles[key] || "";
        });

        if (previousDataAttribute === null) {
          portalRoot.removeAttribute("data-agentic-chat");
        } else {
          portalRoot.setAttribute("data-agentic-chat", previousDataAttribute);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!portalNode) {
      return undefined;
    }

    const accent = colors?.neonB || DEFAULT_COLORS.neonB;
    const borderShade = colors?.border || DEFAULT_COLORS.border;

    const cssVariables = {
      "--tc-text": colors?.text || DEFAULT_COLORS.text,
      "--tc-text-muted": colors?.textMut || DEFAULT_COLORS.textMut,
      "--tc-text-mut": colors?.textMut || DEFAULT_COLORS.textMut,
      "--tc-border": borderShade,
      "--tc-bg-soft": colors?.bgSoft || DEFAULT_COLORS.bgSoft,
      "--tc-bg": colors?.bg || DEFAULT_COLORS.bg,
      "--tc-neon-a": colors?.neonA || DEFAULT_COLORS.neonA,
      "--tc-neon-b": accent,
      "--chat-text": colors?.text || DEFAULT_COLORS.text,
      "--chat-text-muted": colors?.textMut || DEFAULT_COLORS.textMut,
      "--chat-muted": colors?.textMut || DEFAULT_COLORS.textMut,
      "--chat-border": borderShade,
      "--chat-bg": colors?.bg || DEFAULT_COLORS.bg,
      "--chat-bg-elevated": colors?.bgSoft || DEFAULT_COLORS.bgSoft,
      "--chat-accent": accent,
      "--chat-accent-soft": hexToRgba(accent, 0.2) || "rgba(255, 111, 0, 0.2)",
      "--chat-accent-strong": hexToRgba(accent, 0.6) || "rgba(255, 111, 0, 0.6)"
    };

    const previousValues = Object.keys(cssVariables).reduce((acc, key) => {
      acc[key] = portalNode.style.getPropertyValue(key);
      return acc;
    }, {});

    const bodyComputedStyle = document?.body ? window.getComputedStyle(document.body) : null;
    const fontDefaults = {
      fontFamily: bodyComputedStyle?.fontFamily || "'Segoe UI','Roboto','Oxygen','Ubuntu','Cantarell','Fira Sans','Droid Sans','Helvetica Neue',sans-serif",
      fontSize: bodyComputedStyle?.fontSize || "16px",
      lineHeight: bodyComputedStyle?.lineHeight || "1.6",
      color: bodyComputedStyle?.color || colors?.text || DEFAULT_COLORS.text,
    };

    const previousFontValues = {
      fontFamily: portalNode.style.fontFamily,
      fontSize: portalNode.style.fontSize,
      lineHeight: portalNode.style.lineHeight,
      color: portalNode.style.color,
      webkitFontSmoothing: portalNode.style.getPropertyValue("-webkit-font-smoothing"),
      mozOsxFontSmoothing: portalNode.style.getPropertyValue("-moz-osx-font-smoothing"),
    };

    Object.entries(cssVariables).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        portalNode.style.setProperty(key, value);
      }
    });

    Object.entries(fontDefaults).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'color') {
          portalNode.style.color = value;
        } else {
          portalNode.style[key] = value;
        }
      }
    });

    if (bodyComputedStyle?.getPropertyValue('-webkit-font-smoothing')) {
      portalNode.style.setProperty('-webkit-font-smoothing', bodyComputedStyle.getPropertyValue('-webkit-font-smoothing'));
    } else {
      portalNode.style.setProperty('-webkit-font-smoothing', 'antialiased');
    }

    if (bodyComputedStyle?.getPropertyValue('-moz-osx-font-smoothing')) {
      portalNode.style.setProperty('-moz-osx-font-smoothing', bodyComputedStyle.getPropertyValue('-moz-osx-font-smoothing'));
    } else {
      portalNode.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
    }

    return () => {
      Object.keys(cssVariables).forEach((key) => {
        const previousValue = previousValues[key];
        if (previousValue) {
          portalNode.style.setProperty(key, previousValue);
        } else {
          portalNode.style.removeProperty(key);
        }
      });

      if (previousFontValues.fontFamily) {
        portalNode.style.fontFamily = previousFontValues.fontFamily;
      } else {
        portalNode.style.removeProperty('font-family');
      }

      if (previousFontValues.fontSize) {
        portalNode.style.fontSize = previousFontValues.fontSize;
      } else {
        portalNode.style.removeProperty('font-size');
      }

      if (previousFontValues.lineHeight) {
        portalNode.style.lineHeight = previousFontValues.lineHeight;
      } else {
        portalNode.style.removeProperty('line-height');
      }

      if (previousFontValues.color) {
        portalNode.style.color = previousFontValues.color;
      } else {
        portalNode.style.removeProperty('color');
      }

      if (previousFontValues.webkitFontSmoothing) {
        portalNode.style.setProperty('-webkit-font-smoothing', previousFontValues.webkitFontSmoothing);
      } else {
        portalNode.style.removeProperty('-webkit-font-smoothing');
      }

      if (previousFontValues.mozOsxFontSmoothing) {
        portalNode.style.setProperty('-moz-osx-font-smoothing', previousFontValues.mozOsxFontSmoothing);
      } else {
        portalNode.style.removeProperty('-moz-osx-font-smoothing');
      }
    };
  }, [portalNode, colors]);

  useEffect(() => {
    evaluateLayoutMode();
    if (typeof window === "undefined") {
      return undefined;
    }

    window.addEventListener("resize", evaluateLayoutMode);
    window.addEventListener("orientationchange", evaluateLayoutMode);

    return () => {
      window.removeEventListener("resize", evaluateLayoutMode);
      window.removeEventListener("orientationchange", evaluateLayoutMode);
    };
  }, [evaluateLayoutMode]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    if (isChatOpen && isFullScreen && !isMinimized) {
      document.body.classList.add("tc-chat-no-scroll");
    } else {
      document.body.classList.remove("tc-chat-no-scroll");
    }

    return () => {
      document.body.classList.remove("tc-chat-no-scroll");
    };
  }, [isChatOpen, isFullScreen, isMinimized]);

  // Mobile keyboard detection and viewport adjustments
  useEffect(() => {
    if (!MobileTrackingUtils.isMobileDevice()) {
      return undefined;
    }

    const KEYBOARD_OPEN_THRESHOLD = 48;
    let orientationFrameId = null;
    let lastOrientationChange = 0;

    const updateKeyboardState = (keyboardVisible) => {
      if (keyboardVisible !== keyboardStateRef.current) {
        keyboardStateRef.current = keyboardVisible;
        setIsKeyboardOpen(keyboardVisible);
        MobileTrackingUtils.trackKeyboardEvent(
          keyboardVisible ? 'keyboard_open' : 'keyboard_close',
          { chat_is_open: isChatOpen }
        );
      }
    };

    const applyViewportMetrics = (windowHeight, visibleHeight, rawKeyboardHeight) => {
      const normalizedVisibleHeight = Math.max(0, Math.round(visibleHeight));
      const normalizedKeyboardHeight = rawKeyboardHeight > KEYBOARD_OPEN_THRESHOLD ? rawKeyboardHeight : 0;

      setViewportHeight(normalizedVisibleHeight);
      setKeyboardOffset(normalizedKeyboardHeight);
      updateKeyboardState(normalizedKeyboardHeight > 0);

      if (normalizedKeyboardHeight === 0) {
        baselineHeightRef.current = windowHeight;
      }
    };

    const handleViewportChange = () => {
      const viewport = window.visualViewport;
      const windowHeight = window.innerHeight;

      if (viewport) {
        const { height, offsetTop } = viewport;
        const keyboardHeight = Math.max(0, Math.round(windowHeight - (height + offsetTop)));
        applyViewportMetrics(windowHeight, height, keyboardHeight);
        return;
      }

      const elapsedSinceOrientation = Date.now() - lastOrientationChange;
      const rawKeyboardHeight = Math.max(0, Math.round(baselineHeightRef.current - windowHeight));
      const effectiveKeyboardHeight = elapsedSinceOrientation < 400 ? 0 : rawKeyboardHeight;
      applyViewportMetrics(windowHeight, windowHeight, effectiveKeyboardHeight);
    };

    const handleOrientationChange = () => {
      lastOrientationChange = Date.now();
      if (orientationFrameId) {
        cancelAnimationFrame(orientationFrameId);
      }
      orientationFrameId = requestAnimationFrame(() => {
        baselineHeightRef.current = window.innerHeight;
        handleViewportChange();
      });
    };

    handleViewportChange();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    }

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      }
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleOrientationChange);

      if (orientationFrameId) {
        cancelAnimationFrame(orientationFrameId);
      }
    };
  }, [isChatOpen]);

  useEffect(() => {
    if (!isChatOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeChat();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isChatOpen, closeChat]);

  // Track chat open/close events
  useEffect(() => {
    console.debug("AgenticChatLauncher: isChatOpen changed", {
      isChatOpen,
      timestamp: Date.now()
    });

    if (isChatOpen && !chatOpenTimeRef.current) {
      chatOpenTimeRef.current = Date.now();
      trackChatOpen({
        trigger_element: 'chat_launcher_button',
        page_title: document.title,
        referrer: document.referrer || 'direct'
      });
      const sessionId = getChatSessionId();
      trackChatEvent('chat_open', {
        trigger_element: 'chat_launcher_button',
        session_duration_seconds: 0,
        sessionId
      });
      setIsMinimized(false);
      setLiveAnnouncement("Chat window opened");
    } else if (!isChatOpen && chatOpenTimeRef.current) {
      trackChatClose({
        trigger_element: 'close_button',
        session_duration: Math.round((Date.now() - chatOpenTimeRef.current) / 1000)
      });
      const sessionDuration = Math.round((Date.now() - chatOpenTimeRef.current) / 1000);
      trackChatEvent('chat_close', {
        trigger_element: 'close_button',
        session_duration_seconds: sessionDuration
      });
      chatOpenTimeRef.current = null;
      setLiveAnnouncement("Chat closed");
    }
  }, [isChatOpen, layoutMode]);

  // Check if privacy banner should be shown
  useEffect(() => {
    const bannerDismissed = sessionStorage.getItem('privacyBannerDismissed');
    if (bannerDismissed === 'true') {
      setShowPrivacyBanner(false);
    }
  }, []);

  useEffect(() => {
    if (!panelRef.current) return;
    panelRef.current.scrollTop = panelRef.current.scrollHeight;
  }, [messages, typing]);

  useEffect(() => {
    if (!messages.length) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role === "agent") {
      setLiveAnnouncement(`New message from agent: ${lastMessage.text}`);
    }
  }, [messages]);

  useEffect(() => {
    autoSizeTextarea();
  }, [autoSizeTextarea, input, isChatOpen, isMinimized]);

  useEffect(() => {
    if (!isChatOpen || isMinimized) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      textareaRef.current?.focus({ preventScroll: true });
    }, 120);

    return () => window.clearTimeout(focusTimer);
  }, [isChatOpen, isMinimized]);


  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSpeechSupported(false);
      return undefined;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognitionRef.current = recognition;
    setIsSpeechSupported(true);

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript || '';
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const baseInput = speechBaseInputRef.current || '';
      const combined = `${baseInput} ${finalTranscript} ${interimTranscript}`
        .replace(/\s+/g, ' ')
        .trim();
      setInput(combined);
    };

    recognition.onerror = (event) => {
      console.warn('Speech recognition error', event);
      setIsListening(false);
      setErrorMessage('Sorry—something went wrong. Please try again.');
      trackChatEvent('errors', { source: 'speech_recognition', error: event.error });
    };

    recognition.onend = () => {
      setIsListening(false);
      speechBaseInputRef.current = '';
      if (MobileTrackingUtils.isMobileDevice()) {
        MobileTrackingUtils.trackMobileInteraction('voice_input_stop', {
          input_method: 'speech'
        });
      }
    };

    return () => {
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      try {
        recognition.stop();
      } catch (error) {
        // ignore cleanup errors
      }
    };
  }, []);

  useEffect(() => () => {
    if (activeRequestControllerRef.current) {
      activeRequestControllerRef.current.abort();
    }
  }, []);


  const appendAgentText = useCallback((messageId, text) => {
    if (!text) {
      return;
    }

    setMessages((prev) => prev.map((message) => {
      if (message.id !== messageId) {
        return message;
      }

      const previousText = message.text || '';
      const mergedText = mergeBlockText(previousText, text);
      if (mergedText === previousText) {
        return { ...message, heartbeat: false };
      }

      return { ...message, text: mergedText, heartbeat: false };
    }));
  }, []);

  const finalizeAgentMessage = useCallback((messageId, fallbackText = '') => {
    setMessages((prev) => prev.map((message) => {
      if (message.id !== messageId) {
        return message;
      }
      return {
        ...message,
        streaming: false,
        text: message.text || fallbackText,
        timestamp: message.timestamp || formatTimestamp()
      };
    }));
  }, []);

  const send = useCallback(async (text, { isQuickReply = false } = {}) => {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      return;
    }

    const sessionId = getChatSessionId();
    const userMessage = createChatMessage('user', trimmed);

    activeRequestControllerRef.current?.abort();

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setErrorMessage('');
    setTyping(true);
    setIsStreaming(true);

    trackMessageSent(trimmed, {
      input_method: isQuickReply ? 'quick_reply' : 'manual_input',
      device_type: getDeviceType(),
      keyboard_was_open: isKeyboardOpen,
      character_count: trimmed.length,
      word_count: trimmed.split(/\s+/).length
    });

    if (isQuickReply) {
      trackQuickReply(trimmed, {
        quick_reply_position: quickReplies.indexOf(trimmed) + 1,
        total_quick_replies: quickReplies.length,
        action: 'send'
      });
    }

    trackChatEvent('chat_send', {
      input_method: isQuickReply ? 'quick_reply' : 'manual_input',
      message_length: trimmed.length,
      preview: trimmed.slice(0, 120)
    });

    onSend(trimmed);

    if (MobileTrackingUtils.isMobileDevice() && textareaRef.current) {
      textareaRef.current.blur();
    }

    const agentMessageId = generateMessageId('agent');
    const heartbeatTimers = {};
    let agentResponseText = '';
    const agentMessage = createChatMessage('agent', '', { id: agentMessageId, streaming: true });
    setMessages((prev) => [...prev, agentMessage]);
    setHeartbeatTimer(agentMessageId);

    const history = buildHistory([...messagesRef.current, userMessage]);
    const fallbackChatApiBase = "https://twstcld-api-agentic-agents.azurewebsites.net";
    const apiBase = (process.env.REACT_APP_CHAT_API_URL || fallbackChatApiBase).replace(/\/$/, '');
    const endpoint = `${apiBase}/api/agent-chat`;

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'x-chat-session': sessionId
    };

    if (process.env.REACT_APP_CHAT_FUNCTION_KEY) {
      headers['x-functions-key'] = process.env.REACT_APP_CHAT_FUNCTION_KEY;
    }

    const controller = new AbortController();
    activeRequestControllerRef.current = controller;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: history
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream') && response.body && response.body.getReader) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let streamComplete = false;

        while (!streamComplete) {
          const { value, done } = await reader.read();
          if (done) {
            streamComplete = true;
            break;
          }
          buffer += decoder.decode(value, { stream: true });

          let separatorIndex = buffer.indexOf('\n\n');
          while (separatorIndex !== -1) {
            const chunk = buffer.slice(0, separatorIndex).trim();
            buffer = buffer.slice(separatorIndex + 2);
            streamComplete = processSseChunk(chunk, agentMessageId);
            if (streamComplete) {
              break;
            }
            separatorIndex = buffer.indexOf('\n\n');
          }
        }

        if (!streamComplete && buffer.trim()) {
          processSseChunk(buffer.trim(), agentMessageId);
        }

      } else {
        const fallbackText = await parseFallbackResponse(response);
        appendAgentText(agentMessageId, fallbackText);
        agentResponseText += fallbackText;
      }

      clearHeartbeatTimer(agentMessageId);
      finalizeAgentMessage(agentMessageId);
      setMessages((prev) => prev.map((m) => (m.id === agentMessageId ? { ...m, heartbeat: false } : m)));
      setTyping(false);
      setIsStreaming(false);

      const finalText = agentResponseText || getMessageText(messagesRef.current, agentMessageId);
      trackChatEvent('chat_response_ok', {
        message_length: (finalText || '').length,
        preview: (finalText || '').slice(0, 120)
      });
    } catch (error) {
      clearHeartbeatTimer(agentMessageId);
      if (error.name === 'AbortError') {
        setTyping(false);
        setIsStreaming(false);
        setMessages((prev) => prev.filter((message) => message.id !== agentMessageId));
        return;
      }

      console.error('Chat request failed', error);
      setTyping(false);
      setIsStreaming(false);
      setErrorMessage('Sorry—something went wrong. Please try again.');
      trackChatEvent('errors', {
        source: 'chat_response',
        error: error.message
      });

      setMessages((prev) => prev.map((message) => (
        message.id === agentMessageId
          ? { ...message, text: 'Sorry—something went wrong. Please try again.', streaming: false }
          : message
      )));
    } finally {
      clearHeartbeatTimer(agentMessageId);
      activeRequestControllerRef.current = null;
    }

    function clearHeartbeatTimer(id) {
      if (heartbeatTimers[id]) {
        clearTimeout(heartbeatTimers[id]);
        delete heartbeatTimers[id];
      }
    }

    function setHeartbeatTimer(id) {
      clearHeartbeatTimer(id);
      heartbeatTimers[id] = setTimeout(() => {
        setMessages((prev) => prev.map((m) => {
          if (m.id !== id || m.streaming === false) {
            return m;
          }
          return { ...m, heartbeat: true };
        }));
      }, 15000);
    }

    function processSseChunk(chunk, messageId) {
      if (!chunk) {
        return false;
      }

      const lines = chunk.split('\n').filter(Boolean);
      let dataPayload = '';
      let eventName = null;

      lines.forEach((line) => {
        if (line.startsWith('event:')) {
          eventName = line.replace(/^event:\s*/, '').trim();
        } else if (line.startsWith('data:')) {
          dataPayload += `${line.replace(/^data:\s*/, '')}\n`;
        }
      });

      const dataString = dataPayload.trim();
      if (!dataString && !eventName) {
        return false;
      }

      if (dataString === '[DONE]' || eventName === 'complete' || eventName === 'done') {
        clearHeartbeatTimer(messageId);
        return true;
      }

      if (eventName === 'error') {
        let errorPayload = dataString;
        try {
          const parsedError = dataString ? JSON.parse(dataString) : {};
          errorPayload = parsedError?.message || parsedError?.error || parsedError?.detail || errorPayload;
        } catch (err) {
          // fall through with raw payload
        }

        clearHeartbeatTimer(messageId);
        setMessages((prev) => prev.map((m) => (m.id === messageId ? {
          ...m,
          error: errorPayload || 'The agent encountered an error while responding.',
          streaming: false,
          heartbeat: false
        } : m)));
        setTyping(false);
        setIsStreaming(false);
        return false;
      }

      try {
        const parsed = dataString ? JSON.parse(dataString) : {};

        const isErrorEvent = parsed?.type === 'error' || parsed?.event === 'error' || parsed?.error;
        if (isErrorEvent) {
          const message = typeof parsed === 'string'
            ? parsed
            : parsed?.message || parsed?.error || parsed?.detail || 'The agent encountered an error while responding.';

          clearHeartbeatTimer(messageId);
          setMessages((prev) => prev.map((m) => (m.id === messageId ? {
            ...m,
            error: message,
            streaming: false,
            heartbeat: false
          } : m)));
          setTyping(false);
          setIsStreaming(false);
          return false;
        }

        // Structured block events
        if (isStructuredBlockEvent(parsed)) {
          // normalize and merge blocks into message
          setMessages((prev) => prev.map((m) => {
            if (m.id !== messageId) return m;
            try {
              const nextBlocks = mergeBlocks(m.blocks || [], parsed);
              // update fallback text too
              const fallback = blocksToFallbackText(nextBlocks);
              agentResponseText = fallback;
              return { ...m, blocks: nextBlocks, text: fallback, heartbeat: false };
            } catch (e) {
              console.warn('Failed to merge structured block', e, parsed);
              return m;
            }
          }));

          // reset heartbeat timer and set a new one
          clearHeartbeatTimer(messageId);
          setHeartbeatTimer(messageId);
          return false;
        }

        let deltaText = '';
        if (typeof parsed === 'string') {
          deltaText = parsed;
        } else if (typeof parsed?.delta === 'string') {
          deltaText = parsed.delta;
        } else if (typeof parsed?.data === 'string') {
          deltaText = parsed.data;
        } else if (typeof parsed?.text === 'string') {
          deltaText = parsed.text;
        } else if (typeof parsed?.message === 'string') {
          deltaText = parsed.message;
        } else if (typeof parsed?.content === 'string') {
          deltaText = parsed.content;
        } else if (typeof parsed?.detail === 'string') {
          deltaText = parsed.detail;
        }

        if (!deltaText) {
          deltaText = extractStreamText(parsed);
        }

        if (deltaText) {
          appendAgentText(messageId, deltaText);
          agentResponseText += deltaText;
          clearHeartbeatTimer(messageId);
          setHeartbeatTimer(messageId);
        }
      } catch (parseError) {
        // fallback: treat raw data as text
        appendAgentText(messageId, dataString);
        agentResponseText += dataString;
        clearHeartbeatTimer(messageId);
        setHeartbeatTimer(messageId);
      }

      return false;
    }

    async function parseFallbackResponse(res) {
      const responseType = res.headers.get('content-type') || '';
      if (responseType.includes('application/json')) {
        const clone = res.clone();
        const json = await clone.json();
        if (typeof json === 'string') {
          return json;
        }

        return json.reply || json.message || json.answer || json.response || JSON.stringify(json);
      }

      return await res.text();
    }
  }, [appendAgentText, finalizeAgentMessage, isKeyboardOpen, onSend, quickReplies]);

  const handleTextareaKeyDown = useCallback((event) => {
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    if (event.nativeEvent?.isComposing) {
      return;
    }

    event.preventDefault();
    send(input);
  }, [input, send]);

  const handleTextareaInput = useCallback(() => {
    autoSizeTextarea();
  }, [autoSizeTextarea]);

  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreen((previous) => {
      const next = !previous;
      setLiveAnnouncement(next ? "Chat expanded to full screen" : "Chat returned to windowed view");
      return next;
    });
  }, []);

  const handleRestore = useCallback(() => {
    setIsMinimized(false);
    setLiveAnnouncement("Chat restored");
  }, []);

  const handleClose = useCallback(() => {
    setIsMinimized(false);
    setIsFullScreen(false);
    setIsKeyboardOpen(false);
    setKeyboardOffset(0);
    keyboardStateRef.current = false;
    baselineHeightRef.current = typeof window === "undefined" ? 0 : window.innerHeight;
    closeChat();
  }, [closeChat]);

  const handleOverlayClick = useCallback(() => {
    if (!isFullScreen) {
      return;
    }

    if (layoutMode === "mobile") {
      handleClose();
      return;
    }

    setIsFullScreen(false);
    setLiveAnnouncement("Chat returned to windowed view");
  }, [handleClose, isFullScreen, layoutMode]);

  // Handle privacy banner dismissal
  const handlePrivacyBannerDismiss = () => {
    setShowPrivacyBanner(false);
    sessionStorage.setItem('privacyBannerDismissed', 'true');
  };

  const handleQuickReply = (text) => {
    if (!text || isStreaming) {
      return;
    }

    setIsMinimized(false);

    if (MobileTrackingUtils.isMobileDevice()) {
      MobileTrackingUtils.trackMobileInteraction('quick_reply_send', {
        quick_reply_text: text,
        button_size: '44x44px',
        touch_target_compliant: true
      });
    }

    send(text, { isQuickReply: true });
  };

  const handleVoiceInput = () => {
    if (!isSpeechSupported || !recognitionRef.current) {
      return;
    }

    const recognition = recognitionRef.current;

    if (!isListening) {
      speechBaseInputRef.current = input;
      setErrorMessage('');
      trackChatEvent('chat_mic_used', {
        browser: navigator.userAgent
      });

      if (MobileTrackingUtils.isMobileDevice()) {
        MobileTrackingUtils.trackMobileInteraction('voice_input_start', {
          input_method: 'speech',
          button_size: '32x32px'
        });
      }

      try {
        recognition.start();
      } catch (error) {
        console.warn('Speech recognition start failed', error);
        setErrorMessage('Sorry—something went wrong. Please try again.');
        trackChatEvent('errors', {
          source: 'speech_recognition',
          error: error.message
        });
      }
    } else {
      recognition.stop();
    }
  };

  const wrapperClassNames = `tc-chat-wrapper ${
    isFullScreen ? "fullscreen" : layoutMode === "tablet" ? "compact-tablet" : "compact-desktop"
  }`;

  const isMobileDevice = MobileTrackingUtils.isMobileDevice();
  const baseBottomSpacing = 'calc(1.5rem + var(--tc-safe-area-bottom, 0px))';
  const keyboardBottomSpacing = `calc(${Math.max(0, Math.round(keyboardOffset + 12))}px + var(--tc-safe-area-bottom, 0px))`;
  const shouldElevateForKeyboard = isKeyboardOpen && isMobileDevice;
  const dynamicBottom = shouldElevateForKeyboard
    ? keyboardBottomSpacing
    : (!isFullScreen ? baseBottomSpacing : undefined);
  const dynamicHeight = isFullScreen && isKeyboardOpen && isMobileDevice && viewportHeight
    ? `${Math.max(360, Math.round(viewportHeight - 8))}px`
    : undefined;
  const dynamicMaxHeight = !isFullScreen && isKeyboardOpen && isMobileDevice && viewportHeight
    ? `${Math.max(320, Math.round(viewportHeight - 16))}px`
    : undefined;
  const dynamicQuickReplyMaxHeight = shouldElevateForKeyboard && viewportHeight
    ? `${Math.max(56, Math.min(160, Math.round(viewportHeight * 0.3)))}px`
    : undefined;

  const overlayActive = isChatOpen && isFullScreen && !isMinimized;
  const showLauncher = !isChatOpen;
  const showQuickReplies = messages.length === 1 && messages[0]?.role === "agent";

  if (!portalNode) {
    return null;
  }

  return createPortal(
    <>
      <div
        className={`tc-chat-overlay ${overlayActive ? "is-active" : ""}`}
        role="presentation"
        onClick={handleOverlayClick}
      />
      <div className="tc-chat-launcher-container">
        {showLauncher && (
          <button
            id="chat-launcher-button"
            className="tc-chat-launcher-button"
            aria-label="Open TwistedCloud Agent"
            onClick={() => {
              if (MobileTrackingUtils.isMobileDevice()) {
                MobileTrackingUtils.trackMobileInteraction('chat_launcher_tap', {
                  button_size: '56x56px',
                  touch_target_compliant: true
                });
              }

              if (isMinimized) {
                handleRestore();
              } else {
                openChat({
                  cta_id: 'open_chat_launcher',
                  cta_text: 'Open TwistedCloud Agent',
                  section_id: 'chat_launcher',
                  cta_location: 'floating_button',
                  trigger: 'chat_launcher_button'
                });
              }
            }}
          >
            <img src={avatarSrc} alt="Open chat" className="tc-chat-header__avatar" />
          </button>
        )}
      </div>

      {isChatOpen && !isMinimized && (
        <div
          className={wrapperClassNames}
          role="dialog"
          aria-modal={isFullScreen && layoutMode === 'mobile'}
          aria-label={title}
          style={{
            bottom: dynamicBottom,
            maxHeight: dynamicMaxHeight,
            height: dynamicHeight
          }}
        >
          <div className="tc-chat-announcer" aria-live="polite">
            {liveAnnouncement}
          </div>
          <header className="tc-chat-header">
            <img src={avatarSrc} alt="TwistedCloud Agent" className="tc-chat-header__avatar" />
            <div className="tc-chat-header__stack">
              <div className="tc-chat-header__top">
                <div className="tc-chat-header__actions">
                  <button
                    type="button"
                    className={`tc-chat-header__chip ${showTimestamps ? 'is-active' : ''}`}
                    onClick={() => setShowTimestamps((prev) => !prev)}
                  >
                    {showTimestamps ? 'Hide Times' : 'Show Times'}
                  </button>
                  <IconButton
                    label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
                    onClick={handleToggleFullScreen}
                  >
                    {isFullScreen ? <CollapseGlyph /> : <ExpandGlyph />}
                  </IconButton>
                  <IconButton
                    label="Close chat"
                    onClick={() => {
                      if (MobileTrackingUtils.isMobileDevice()) {
                        MobileTrackingUtils.trackMobileInteraction('chat_close_tap', {
                          button_size: '32x32px',
                          touch_target_compliant: false,
                          session_duration: chatOpenTimeRef.current
                            ? Math.round((Date.now() - chatOpenTimeRef.current) / 1000)
                            : 0
                        });
                      }
                      handleClose();
                    }}
                  >
                    <CloseGlyph />
                  </IconButton>
                </div>
              </div>
              <div className="tc-chat-header__meta">
                <span className="tc-chat-header__title">{title}</span>
                <span className="tc-chat-header__subtitle">Always-on, privacy-first assistant</span>
              </div>
            </div>
          </header>

          {showPrivacyBanner && (
            <div className="tc-chat-privacy-banner" role="region" aria-label="Privacy notice">
              <p>
                By proceeding, you agree that Twisted Cloud Solutions can process personal information in accordance
                with our{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Statement
                </a>
                .
              </p>
              <button
                type="button"
                className="tc-chat-privacy-dismiss"
                onClick={handlePrivacyBannerDismiss}
                aria-label="Dismiss privacy notice"
              >
                ×
              </button>
            </div>
          )}

          <div
            ref={panelRef}
            className="tc-chat-messages"
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            style={{
              paddingBottom: shouldElevateForKeyboard ? 'calc(1.5rem + var(--tc-safe-area-bottom, 0px))' : undefined
            }}
          >
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                role={message.role}
                message={message}
                timestamp={message.timestamp}
                showTimestamp={showTimestamps}
              >
                {message.text}
              </MessageBubble>
            ))}
            {typing && <TypingBubble />}
          </div>

          {showQuickReplies && (
            <div
              className="tc-chat-quick-replies"
              role="group"
              aria-label="Suggested replies"
              style={{
                maxHeight: dynamicQuickReplyMaxHeight,
                paddingBottom: shouldElevateForKeyboard ? 'calc(0.75rem + var(--tc-safe-area-bottom, 0px))' : undefined
              }}
            >
              {quickReplies.map((reply) => (
                <button
                  key={reply}
                  type="button"
                  className="tc-chat-quick-reply"
                  onClick={() => handleQuickReply(reply)}
                  onTouchStart={() => {
                    if (MobileTrackingUtils.isMobileDevice()) {
                      MobileTrackingUtils.trackMobileInteraction('quick_reply_touch', {
                        quick_reply_text: reply,
                        button_size: '44x44px',
                        touch_target_compliant: true
                      });
                    }
                  }}
                >
                  {reply}
                </button>
              ))}
            </div>
          )}

          {errorMessage && (
            <div className="tc-chat-error" role="alert" aria-live="assertive">
              {errorMessage}
            </div>
          )}

          <p className="tc-chat-privacy">
            Your chat is protected by Twisted Cloud Solutions'
            {' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Statement</a>.
          </p>

          <form
            className="tc-chat-inputbar"
            onSubmit={(event) => {
              event.preventDefault();
              send(input);
            }}
            style={{
              paddingBottom: isKeyboardOpen && isMobileDevice ? 'calc(1.25rem + var(--tc-safe-area-bottom, 0px))' : undefined
            }}
          >
            <textarea
              ref={textareaRef}
              className="tc-chat-textarea"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onInput={handleTextareaInput}
              onKeyDown={handleTextareaKeyDown}
              placeholder={placeholder}
              aria-label={placeholder}
              rows={1}
            />
            {isSpeechSupported && (
              <IconButton
                label={isListening ? 'Stop voice input' : 'Start voice input'}
                ariaPressed={isListening}
                onClick={handleVoiceInput}
              >
                <MicrophoneIcon size={18} isListening={isListening} />
              </IconButton>
            )}
            <button
              type="submit"
              className="tc-chat-send"
              aria-label="Send message"
              disabled={!input.trim() || isStreaming}
              onTouchStart={() => {
                if (MobileTrackingUtils.isMobileDevice()) {
                  MobileTrackingUtils.trackMobileInteraction('send_button_touch', {
                    message_length: input.length,
                    button_size: '44x44px',
                    touch_target_compliant: true
                  });
                }
              }}
            >
              <PlayGlyph />
            </button>
          </form>
        </div>
      )}

      {isChatOpen && isMinimized && (
        <button
          type="button"
          className="tc-chat-minimized-badge"
          onClick={handleRestore}
          aria-label="Restore chat"
        >
          <PlayGlyph size={16} />
          <span>Chat with Twisted Cloud</span>
        </button>
      )}
    </>,
    portalNode
  );
}

function MessageBubble({ role, message, children, timestamp, showTimestamp = false }) {
  const isAgent = role === "agent";
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 20);
    return () => clearTimeout(timer);
  }, []);

  const hasStructuredBlocks = Array.isArray(message?.blocks) && message.blocks.length > 0;
  const fallbackNode = message?.text ?? children ?? null;
  const fallbackString = typeof fallbackNode === "string" ? fallbackNode : "";
  const hasInlineText = fallbackString.trim().length > 0;
  const hasCustomNode =
    React.isValidElement(fallbackNode) || (Array.isArray(fallbackNode) && fallbackNode.length > 0);
  const hasRenderableFallback =
    hasInlineText || hasCustomNode || (fallbackNode !== null && fallbackNode !== undefined && fallbackNode !== "");

  let bubbleContent = null;
  if (hasStructuredBlocks) {
    bubbleContent = (
      <StructuredBlocks
        blocks={message.blocks}
        fallbackText={hasInlineText ? fallbackString : ""}
      />
    );
  } else if (hasRenderableFallback) {
    bubbleContent = fallbackNode;
  }

  const showHeartbeat = Boolean(message?.streaming && message?.heartbeat);
  const shouldRenderBubble =
    bubbleContent !== null && bubbleContent !== undefined && !(typeof bubbleContent === "string" && bubbleContent.trim() === "");
  const bubbleClasses = [
    "tc-chat-message__bubble"
  ]
    .filter(Boolean)
    .join(" ");

  if (!shouldRenderBubble && !showHeartbeat && !message?.error && !showTimestamp) {
    return null;
  }

  return (
    <div
      className={`tc-chat-message-row ${isAgent ? "agent" : "user"}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 0.3s ease, transform 0.3s ease"
      }}
    >
      {isAgent && (
        <img src={avatarSrc} alt="TwistedCloud Agent" className="tc-chat-avatar" />
      )}
      <div className="tc-chat-message">
        {shouldRenderBubble && (
          <div className={bubbleClasses}>
            {bubbleContent}
          </div>
        )}
        {showHeartbeat && (
          <div className="tc-chat-message__heartbeat" aria-live="polite">
            <span className="tc-chat-message__heartbeat-dot" />
            <span>Still working on it…</span>
          </div>
        )}
        {message?.error && (
          <div className="tc-chat-message__error" role="alert">
            {message.error}
          </div>
        )}
        {showTimestamp && timestamp && (
          <div className="tc-chat-message__meta" aria-hidden>
            {timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="tc-chat-message-row agent">
      <img src={avatarSrc} alt="TwistedCloud Agent typing" className="tc-chat-avatar" />
      <div className="tc-chat-message">
        <div className="tc-chat-message__bubble typing" aria-hidden>
          <span className="tc-chat-typing-dot" />
          <span className="tc-chat-typing-dot" />
          <span className="tc-chat-typing-dot" />
        </div>
      </div>
    </div>
  );
}

function IconButton({ label, onClick, children, ariaPressed }) {
  return (
    <button
      type="button"
      className="tc-chat-icon-button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={ariaPressed}
      onTouchStart={() => {
        if (MobileTrackingUtils.isMobileDevice()) {
          MobileTrackingUtils.trackTouchTargetCompliance("icon_button", 44, {
            button_type: label.toLowerCase(),
            is_compliant: true
          });
        }
      }}
    >
      {children}
    </button>
  );
}

function ExpandGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 9V4h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 4l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 15v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 20l-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CollapseGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 4H4v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 4l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 20h5v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 20l-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlayGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 3l14 9-14 9V3z" fill="white"/>
    </svg>
  );
}

function MicrophoneIcon({ size = 18, isListening = false }) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      aria-hidden
      style={{
        color: isListening ? '#ff4757' : '#6B7280',
        transition: 'color 0.2s ease-in-out'
      }}
    >
      <path 
        d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" 
        fill="currentColor"
      />
      <path 
        d="M19 10v2a7 7 0 0 1-14 0v-2" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
      />
      <path 
        d="M12 19v4M8 23h8" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      {isListening && (
        <g>
          <circle cx="12" cy="12" r="10" fill="none" stroke="#ff4757" strokeWidth="1" opacity="0.3">
            <animate attributeName="r" values="8;12;8" dur="1.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.3;0.1;0.3" dur="1.5s" repeatCount="indefinite"/>
          </circle>
        </g>
      )}
    </svg>
  );
}

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string") {
    return "";
  }

  const cleaned = hex.replace('#', '');
  if (![3, 6].includes(cleaned.length)) {
    return "";
  }

  const normalized = cleaned.length === 3
    ? cleaned.split('').map((char) => `${char}${char}`).join('')
    : cleaned;

  const numeric = Number.parseInt(normalized, 16);
  if (Number.isNaN(numeric)) {
    return "";
  }

  const r = (numeric >> 16) & 255;
  const g = (numeric >> 8) & 255;
  const b = numeric & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatTimestamp = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const generateMessageId = (role) => `${role}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createChatMessage = (role, text, overrides = {}) => ({
  id: overrides.id || generateMessageId(role),
  role,
  text,
  blocks: overrides.blocks || [],
  timestamp: overrides.timestamp || formatTimestamp(),
  ...overrides
});

const STREAM_ROLE_TOKENS = new Set(['assistant', 'system', 'user', 'tool', 'function']);

function extractStreamText(payload) {
  if (!payload) {
    return '';
  }

  const visited = new WeakSet();
  const queue = [payload];
  let result = '';

  while (queue.length) {
    const item = queue.shift();
    if (item === null || item === undefined) {
      continue;
    }

    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (!trimmed) {
        continue;
      }
      if (trimmed === '[DONE]' || STREAM_ROLE_TOKENS.has(trimmed.toLowerCase())) {
        continue;
      }
      result += item;
      continue;
    }

    if (typeof item === 'number' || typeof item === 'boolean') {
      continue;
    }

    if (Array.isArray(item)) {
      queue.push(...item);
      continue;
    }

    if (typeof item === 'object') {
      if (visited.has(item)) {
        continue;
      }
      visited.add(item);

      if (typeof item.content === 'string' || Array.isArray(item.content)) {
        queue.push(item.content);
      }

      if (typeof item.text === 'string' || Array.isArray(item.text)) {
        queue.push(item.text);
      }

      if (typeof item.message === 'string' || Array.isArray(item.message) || (item.message && typeof item.message === 'object')) {
        queue.push(item.message);
      }

      if (typeof item.data === 'string' || Array.isArray(item.data) || (item.data && typeof item.data === 'object')) {
        queue.push(item.data);
      }

      if (typeof item.delta === 'string' || Array.isArray(item.delta) || (item.delta && typeof item.delta === 'object')) {
        queue.push(item.delta);
      }

      if (typeof item.response === 'string' || Array.isArray(item.response) || (item.response && typeof item.response === 'object')) {
        queue.push(item.response);
      }

      if (typeof item.detail === 'string' || Array.isArray(item.detail) || (item.detail && typeof item.detail === 'object')) {
        queue.push(item.detail);
      }

      if (typeof item.output === 'string' || Array.isArray(item.output) || (item.output && typeof item.output === 'object')) {
        queue.push(item.output);
      }

      if (typeof item.value === 'string' || Array.isArray(item.value) || (item.value && typeof item.value === 'object')) {
        queue.push(item.value);
      }

      if (typeof item.body === 'string' || Array.isArray(item.body) || (item.body && typeof item.body === 'object')) {
        queue.push(item.body);
      }

      if (typeof item.arguments === 'string') {
        queue.push(item.arguments);
      }

      if (item.choices) {
        queue.push(item.choices);
      }

      if (item.tool_calls) {
        queue.push(item.tool_calls);
      }

      if (item.function) {
        queue.push(item.function);
      }

      if (item.payload) {
        queue.push(item.payload);
      }
    }
  }

  return result;
}

const buildHistory = (historyMessages) => {
  const allowedRoles = new Set(['system', 'user', 'assistant']);

  return historyMessages
    .map((message) => {
      const normalizedRole = message.role === 'agent' ? 'assistant' : message.role;
      if (!allowedRoles.has(normalizedRole)) {
        return null;
      }

      return {
        role: normalizedRole,
        content: message.text
      };
    })
    .filter(Boolean)
    .slice(-12);
};

const getMessageText = (messages, messageId) => {
  if (!Array.isArray(messages)) {
    return '';
  }
  const found = messages.find((message) => message.id === messageId);
  return found?.text || '';
};
